import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';

import { withTheme } from 'common/styling/theme';
import { getCookie, setCookie } from 'common/utils/helpers';
import { PageTabs, TranslatedNavLink } from 'common/components';
import { getCountries } from 'client/KYC/_redux';
import { TwoStepRegisterModal } from 'auth/twoStepAuth/_components';
import { increaseInvitationCodeClicks } from 'auth/login/api';

import RegistrationForm from '../_components/RegistrationForm';
import ThankForRegistration from '../_components/ThankForRegistration';
import { staticStyles, getDynamicStyles } from './style';

const RegistrationPage = ({
  isEmailConfirmationEnabled,
  isCorporateAccountEnabled,
  location,
  history,
  telegramAuthError,
  theme,
  partnershipConfig,
  countries,
  countriesAreLoaded,
  getCountries,
  tfAuthSettings,
}) => {
  const { partnerCodeParamName } = partnershipConfig;
  const { enabled: isTfaEnabled, mandatory: isTfaRequired } = tfAuthSettings;
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const partnerCodeCookie = getCookie(partnerCodeParamName);

  /* eslint-disable */
  const [isRegistered, setRegistered] = useState(false);
  const [isTfaModalOpened, setIsTfaModalOpened] = useState(false);
  const { telegramAuth, click_id: clickId } = queryString.parse(location.search);
  const queryPartnerCode = queryString.parse(location.search)[partnerCodeParamName];
  const partnerCode = queryPartnerCode || localStorage.getItem('partnerCode') || partnerCodeCookie;
  const querySalesCode = queryString.parse(location.search).sales_code;
  const salesCode = querySalesCode || localStorage.getItem('salesCode');
  const queryShare = queryString.parse(location.search).share;
  const share = queryShare || localStorage.getItem('share');
  const isTelegramAuth = telegramAuth === 'true' || telegramAuthError;
  const authSearch = queryString.stringify({
    ...(salesCode ? { sales_code: salesCode } : {}),
    ...(share ? { share } : {}),
    ...(isTelegramAuth ? { telegramAuth: true } : {}),
  });
  const loginUrl = `/auth/login${authSearch ? `?${authSearch}` : ''}`;
  const registrationUrl = `/auth/register${authSearch ? `?${authSearch}` : ''}`;
  const telegramDataJson = sessionStorage.getItem('telegramData');
  const telegramData = JSON.parse(telegramDataJson || '{}');
  const initialValues =
    telegramData && isTelegramAuth
      ? {
          firstName: telegramData.telegramFirstName ?? '',
          lastName: telegramData.telegramLastName ?? '',
          partnerCode: partnerCode,
          ...(share ? { share } : {}),
          // 只有当salesCode存在时，才添加到initialValues中
          ...(salesCode ? { salesCode } : {}),
        }
      : {
          partnerCode: partnerCode,
          ...(share ? { share } : {}),
          // 只有当salesCode存在时，才添加到initialValues中
          ...(salesCode ? { salesCode } : {}),
        };

  /* eslint-enable */

  const handleTfaModalToggle = () => {
    setIsTfaModalOpened(isOpened => !isOpened);
  };

  useEffect(() => {
    if (clickId) {
      setCookie('click_id', clickId, 30);
    }
  }, []);

  useEffect(() => {
    getCountries();
  }, [getCountries]);

  // Count invitation link clicks when the link lands directly on the registration page (same as LoginPage)
  useEffect(() => {
    if (salesCode) {
      const increaseClicks = async () => {
        try {
          await increaseInvitationCodeClicks(salesCode);
        } catch (error) {
          // Ignore errors, do not affect the registration flow
          console.error('Failed to increase invitation code clicks:', error);
        }
      };

      increaseClicks();
    }
  }, [salesCode]);

  useEffect(() => {
    if (isEmailConfirmationEnabled && isRegistered) {
      history.push('/auth/register/check-email');
    }
  }, [isRegistered, isEmailConfirmationEnabled]);

  return (
    <>
      <PageTabs forAuth>
        <TranslatedNavLink navLinkId="registration-page-nav-to-login-page" id="authLogin" to={loginUrl} />
        <TranslatedNavLink
          navLinkId="registration-page-nav-to-registration-page-active"
          id="authRegister"
          to={registrationUrl}
        />
      </PageTabs>
      {isTelegramAuth && !isRegistered && (
        <div className="Auth__message">
          <FormattedMessage id="telegramRegisterMessage">{txt => <p>{txt}</p>}</FormattedMessage>
        </div>
      )}
      {isEmailConfirmationEnabled && isRegistered ? (
        <ThankForRegistration />
      ) : (
        <RegistrationForm
          initialValues={initialValues}
          displayThankBlock={() => setRegistered(true)}
          displayTfaModal={handleTfaModalToggle}
          isTfaRequired={isTfaEnabled && isTfaRequired}
          isCorporate={isCorporateAccountEnabled}
          isEmailConfirmationEnabled={isEmailConfirmationEnabled}
          history={history}
          isTelegramAuth={isTelegramAuth}
          telegramData={telegramData}
          countries={countries}
          countriesAreLoaded={countriesAreLoaded}
        />
      )}
      {isTfaEnabled && isTfaRequired && (
        <TwoStepRegisterModal isOpen={isTfaModalOpened} onRequestClose={handleTfaModalToggle} />
      )}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </>
  );
};

RegistrationPage.propTypes = {
  isEmailConfirmationEnabled: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  countriesAreLoaded: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  telegramAuthError: PropTypes.bool.isRequired,
  isCorporateAccountEnabled: PropTypes.bool,
  tfAuthSettings: PropTypes.object,
  theme: PropTypes.object,
};

RegistrationPage.defaultProps = {
  tfAuthSettings: {},
  location: {},
  theme: {},
  isCorporateAccountEnabled: false,
};
export default compose(
  withRouter,
  withTheme(),
  connect(
    state => ({
      isEmailConfirmationEnabled: state.interfaceConfig.emailConfirmation.enabled,
      isCorporateAccountEnabled: state.interfaceConfig?.kycSettings?.corporate_account_enabled,
      tfAuthSettings: state.interfaceConfig.tfAuthSettings,
      telegramAuthError: state.auth.registration.telegramAuthError,
      partnershipConfig: state.interfaceConfig.partnership,
      countries: state.client.kyc.countries,
      countriesAreLoaded: state.client.kyc.countriesAreLoaded,
    }),
    {
      getCountries: () => getCountries.request(),
    }
  )
)(RegistrationPage);
export { RegistrationPage };
